"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CoseKey = exports.CoseMessage = exports.decoders = exports.EMPTY = exports.ANONYMOUS = void 0;
const cbor_1 = __importDefault(require("cbor"));
const js_sha3_1 = require("js-sha3");
const identity_1 = require("../identity");
const cbor_2 = require("./cbor");
const const_1 = require("../const");
exports.ANONYMOUS = Buffer.from([0x00]);
exports.EMPTY = Buffer.alloc(0);
exports.decoders = {
    tags: {
        10000: (value) => new identity_1.Address(Buffer.from(value)),
        1: (value) => (0, cbor_2.tag)(1, value),
    },
};
class CoseMessage {
    constructor(protectedHeader, unprotectedHeader, content, signature) {
        this.protectedHeader = protectedHeader;
        this.unprotectedHeader = unprotectedHeader;
        this.content = content;
        this.signature = signature;
    }
    static fromCborData(data) {
        var _a;
        const cose = cbor_1.default.decodeFirstSync(data, exports.decoders).value;
        const protectedHeader = ((_a = cose[0]) === null || _a === void 0 ? void 0 : _a.length)
            ? cbor_1.default.decodeFirstSync(cose[0])
            : cose[0];
        const unprotectedHeader = cose[1];
        const content = cbor_1.default.decodeFirstSync(cose[2], exports.decoders).value;
        const signature = cose[3];
        return new CoseMessage(protectedHeader, unprotectedHeader, content, signature);
    }
    static async fromMessage(message, identity = new identity_1.AnonymousIdentity()) {
        const protectedHeader = await this.getProtectedHeader(identity);
        const cborProtectedHeader = cbor_1.default.encodeCanonical(protectedHeader);
        const content = message.getContent();
        const cborContent = cbor_1.default.encodeOne((0, cbor_2.tag)(10001, content), {
            highWaterMark: const_1.HIGH_WATER_MARK,
        });
        const toBeSigned = cbor_1.default.encodeOne(["Signature1", cborProtectedHeader, exports.EMPTY, cborContent], { highWaterMark: const_1.HIGH_WATER_MARK });
        const unprotectedHeader = await identity.getUnprotectedHeader(cborContent, cborProtectedHeader);
        const signature = await identity.sign(toBeSigned, unprotectedHeader);
        return new CoseMessage(protectedHeader, unprotectedHeader, content, signature);
    }
    static async getProtectedHeader(identity) {
        let protectedHeader = new Map();
        if ("getCoseKey" in identity) {
            const coseKey = identity.getCoseKey();
            protectedHeader.set(1, coseKey.key.get(3)); // alg
            protectedHeader.set(4, coseKey.keyId); // kid: kid
            protectedHeader.set("keyset", coseKey.toCborData());
            if (typeof (identity === null || identity === void 0 ? void 0 : identity.getProtectedHeader) === "function") {
                protectedHeader = new Map([
                    ...protectedHeader,
                    ...(await identity.getProtectedHeader()),
                ]);
            }
        }
        return protectedHeader;
    }
    replacer(key, value) {
        if ((value === null || value === void 0 ? void 0 : value.type) === "Buffer") {
            return Buffer.from(value.data).toString("hex");
        }
        else if (value instanceof Map) {
            return Object.fromEntries(value.entries());
        }
        else if (typeof value === "bigint") {
            return parseInt(value.toString());
        }
        else if (key === "hash") {
            return Buffer.from(value).toString("hex");
        }
        else {
            return value;
        }
    }
    toCborData() {
        const payload = cbor_1.default.encodeOne((0, cbor_2.tag)(10001, this.content), {
            highWaterMark: const_1.HIGH_WATER_MARK,
        });
        const protectedHeader = cbor_1.default.encodeOne(this.protectedHeader);
        return cbor_1.default.encodeOne((0, cbor_2.tag)(18, [
            protectedHeader,
            this.unprotectedHeader,
            payload,
            this.signature,
        ]), { highWaterMark: const_1.HIGH_WATER_MARK });
    }
    toString() {
        return JSON.stringify([
            this.protectedHeader,
            this.unprotectedHeader,
            this.content,
            this.signature,
        ], this.replacer, 2);
    }
}
exports.CoseMessage = CoseMessage;
class CoseKey {
    constructor(commonParams = new Map()) {
        this.common = commonParams;
        this.keyId = this.getKeyId();
        this.key = this.getKey();
    }
    getKeyId() {
        if (Buffer.compare(this.common.get(-2), exports.ANONYMOUS) === 0) {
            return exports.ANONYMOUS;
        }
        const keyId = new Map(this.common);
        const pk = "01" + (0, js_sha3_1.sha3_224)(cbor_1.default.encodeCanonical(keyId));
        return Buffer.from(pk, "hex");
    }
    getKey() {
        const key = new Map(this.common);
        key.set(2, this.keyId); // kid: Key ID
        return key;
    }
    toCborData() {
        // encodeCanonical will encode the map in sorted order
        return cbor_1.default.encodeCanonical([this.key]);
    }
    toAddress() {
        return new identity_1.Address(this.keyId);
    }
}
exports.CoseKey = CoseKey;
